// §6.3 — arrow navigation over a wrapped grid of windows.
//
// In the kernel rather than in a skin because all three switchers render a grid of the same
// thing (§6.3: house "grid of window thumbnails", windows11 "Task View", macOS "Mission
// Control"), and three copies of index arithmetic is three chances to get the last row wrong.

export type GridKey = 'ArrowLeft' | 'ArrowRight' | 'ArrowUp' | 'ArrowDown' | 'Home' | 'End';

export function isGridKey(key: string): key is GridKey {
  return (
    key === 'ArrowLeft' ||
    key === 'ArrowRight' ||
    key === 'ArrowUp' ||
    key === 'ArrowDown' ||
    key === 'Home' ||
    key === 'End'
  );
}

/**
 * The index an arrow moves to, in a grid `columns` wide holding `count` items.
 *
 * Left and right walk the whole sequence and wrap at both ends, so holding one arrow always
 * reaches everything. Up and down move by a row and wrap *within the column*: landing past the
 * end of a ragged last row would otherwise leave the selection nowhere, so it clamps back to the
 * last item instead.
 */
export function moveInGrid(index: number, count: number, columns: number, key: GridKey): number {
  if (count <= 0) return 0;
  const width = Math.max(1, columns);
  const current = Math.min(Math.max(index, 0), count - 1);

  switch (key) {
    case 'Home':
      return 0;
    case 'End':
      return count - 1;
    case 'ArrowLeft':
      return (current - 1 + count) % count;
    case 'ArrowRight':
      return (current + 1) % count;
    case 'ArrowUp':
    case 'ArrowDown': {
      const rows = Math.ceil(count / width);
      const column = current % width;
      const row = Math.floor(current / width);
      const step = key === 'ArrowDown' ? 1 : -1;
      // Walk row by row so a ragged last row is stepped over rather than landed in.
      for (let n = 1; n <= rows; n += 1) {
        const candidate = (((row + step * n) % rows) + rows) % rows;
        const target = candidate * width + column;
        if (target < count) return target;
      }
      return current;
    }
  }
}
